import React from 'react';
import { View, ScrollView, TouchableOpacity, Text, StyleSheet, Image, type ViewStyle, useWindowDimensions } from 'react-native';

/**
 * 热门目的地项配置
 */
export interface HotDestinationItem {
  /** 唯一标识符 */
  key: string;
  /** 目的地名称 */
  label: string;
  /** 目的地图片 URL 或本地路径 */
  imageUrl?: string;
  /** 点击回调 */
  onPress?: () => void;
}

export interface HotDestinationsBarProps {
  /** 栏目标题 */
  title?: string;
  /** "查看更多"链接的文本 */
  moreText?: string;
  /** 目的地项列表 */
  items: HotDestinationItem[];
  /** 点击"查看更多"的回调 */
  onMorePress?: () => void;
  /** 项被点击时的回调 */
  onItemPress?: (key: string, item: HotDestinationItem) => void;
  /** 自定义样式 */
  style?: ViewStyle;
}

/**
 * 热门目的地栏组件
 * 
 * 展示热门旅游目的地的卡片列表，支持横向滚动
 * 
 * @example
 * ```tsx
 * const destinations = [
 *   { key: 'tokyo', label: '东京', imageUrl: 'https://...' },
 *   { key: 'osaka', label: '大阪', imageUrl: 'https://...' },
 * ];
 * 
 * <HotDestinationsBar
 *   title="想去哪里玩?"
 *   moreText="查看更多"
 *   items={destinations}
 *   onMorePress={() => navigate('/destinations')}
 * />
 * ```
 */
const HotDestinationsBar: React.FC<HotDestinationsBarProps> = ({
  title = '想去哪里玩?',
  moreText = '查看更多',
  items,
  onMorePress,
  onItemPress,
  style,
}) => {
  const { width } = useWindowDimensions();
  const isMobile = typeof width === 'number' ? width < 768 : true;
  return (
    <View style={[styles.container, style]}>
      {/* 标题和"查看更多"行 */}
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <View style={styles.titleBar} />
          <Text style={styles.title}>{title}</Text>
        </View>
        {onMorePress && (
          <TouchableOpacity onPress={onMorePress} activeOpacity={0.7}>
            <Text style={styles.moreText}>{moreText}</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* 横向滚动的目的地卡片 */}
      {isMobile ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={16}
          contentContainerStyle={styles.scrollContent}
          bounces={false}
        >
          {items.map((item) => (
            <TouchableOpacity
              key={item.key}
              style={styles.destinationCard}
              onPress={() => {
                item.onPress?.();
                onItemPress?.(item.key, item);
              }}
              activeOpacity={0.8}
            >
              {/* 目的地图片 */}
              {item.imageUrl ? (
                <Image
                  source={{ uri: item.imageUrl }}
                  style={styles.destinationImage}
                />
              ) : (
                <View style={[styles.destinationImage, styles.placeholderImage]} />
              )}

              {/* 目的地名称 */}
              <Text style={styles.destinationLabel} numberOfLines={1}>
                {item.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      ) : (
        <View style={styles.grid}>
          {items.map((item) => (
            <TouchableOpacity
              key={item.key}
              style={styles.destinationCardGrid}
              onPress={() => {
                item.onPress?.();
                onItemPress?.(item.key, item);
              }}
              activeOpacity={0.8}
            >
              {item.imageUrl ? (
                <Image source={{ uri: item.imageUrl }} style={styles.destinationImageGrid} />
              ) : (
                <View style={[styles.destinationImageGrid, styles.placeholderImage]} />
              )}
              <Text style={styles.destinationLabelGrid} numberOfLines={1}>{item.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  moreText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2563EB',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleBar: {
    width: 4,
    height: 20,
    backgroundColor: '#ff6b35',
    borderRadius: 2,
    marginRight: 10,
  },
  scrollContent: {
    paddingHorizontal: 16,
    gap: 12,
  },
  destinationCard: {
    alignItems: 'center',
    width: 160,
  },
  grid: {
    paddingHorizontal: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12,
  },
  destinationCardGrid: {
    width: '48%',
    alignItems: 'center',
    marginBottom: 12,
  },
  destinationImageGrid: {
    width: '100%',
    height: 120,
    borderRadius: 12,
    backgroundColor: '#E5E7EB',
    marginBottom: 8,
    overflow: 'hidden',
  },
  destinationLabelGrid: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    textAlign: 'left',
    width: '100%'
  },
  destinationImage: {
    width: 160,
    height: 90,
    borderRadius: 12,
    backgroundColor: '#E5E7EB',
    marginBottom: 8,
    overflow: 'hidden',
  },
  placeholderImage: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  destinationLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    textAlign: 'center',
    width: 100,
  },
});

export default HotDestinationsBar;
